<template>
	<div class="uds">
		<div class="uds-toolbar">
			<div class="uds-intro">
				Live clients on the <strong>Unified Dashboard</strong> and where their <em>Agendas &amp; Minutes</em>,
				<em>Events</em> and <em>News &amp; Notices</em> archives come from: the theme's predefined page or a Page Builder page.
				<span v-if="generatedAt" class="uds-fetched">Generated {{ fromNow(generatedAt) }}.</span>
			</div>
			<div class="uds-toolbar__actions">
				<button class="j-action-btn" :disabled="!filteredClients.length" title="Download the current table as CSV" @click="exportCsv">
					<font-awesome-icon :icon="['fas', 'download']" class="me-1" />
					Export CSV
				</button>
				<button class="j-action-btn" :disabled="loading" title="Reload statistics" @click="load">
					<font-awesome-icon :icon="['fas', 'sync']" :spin="loading" class="me-1" />
					Reload
				</button>
			</div>
		</div>

		<div v-if="loadError" class="uds-alert mt-2">
			<font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="me-1" />
			{{ loadError }}
		</div>

		<div v-if="loading && !clients.length" class="uds-loading mt-3">
			<font-awesome-icon :icon="['fas', 'spinner']" spin class="me-1" />
			Loading Unified Dashboard statistics…
		</div>

		<!-- Summary tiles -->
		<div v-if="summary" class="uds-tiles mt-3">
			<div class="uds-tile">
				<div class="uds-tile__label">Live clients</div>
				<div class="uds-tile__value">{{ summary.clients }}</div>
				<div class="uds-tile__sub">on the Unified Dashboard</div>
			</div>
			<div class="uds-tile">
				<div class="uds-tile__label">Agendas &amp; Minutes</div>
				<div class="uds-tile__split">
					<button class="uds-split" :class="{ 'is-active': filters.meetings === 'page_builder' }" @click="toggleFilter('meetings', 'page_builder')">
						<span class="uds-split__num uds-split__num--pb">{{ summary.meetings.page_builder }}</span>
						<span class="uds-split__lbl">Page Builder</span>
					</button>
					<button class="uds-split" :class="{ 'is-active': filters.meetings === 'predefined' }" @click="toggleFilter('meetings', 'predefined')">
						<span class="uds-split__num uds-split__num--pre">{{ summary.meetings.predefined }}</span>
						<span class="uds-split__lbl">Predefined</span>
					</button>
				</div>
				<div v-if="summary.meetings.page_builder_without_page" class="uds-tile__sub uds-tile__sub--warn">
					<font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="me-1" />
					{{ summary.meetings.page_builder_without_page }} without a published page using the Meetings element
				</div>
				<div v-else class="uds-tile__sub">every Page Builder client has a Meetings page</div>
			</div>
			<div class="uds-tile">
				<div class="uds-tile__label">Events</div>
				<div class="uds-tile__split">
					<button class="uds-split" :class="{ 'is-active': filters.events === 'page_builder' }" @click="toggleFilter('events', 'page_builder')">
						<span class="uds-split__num uds-split__num--pb">{{ summary.events.page_builder }}</span>
						<span class="uds-split__lbl">Page Builder</span>
					</button>
					<button class="uds-split" :class="{ 'is-active': filters.events === 'predefined' }" @click="toggleFilter('events', 'predefined')">
						<span class="uds-split__num uds-split__num--pre">{{ summary.events.predefined }}</span>
						<span class="uds-split__lbl">Predefined</span>
					</button>
				</div>
				<div v-if="summary.events.page_builder_by_slug" class="uds-tile__sub uds-tile__sub--warn">
					<font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="me-1" />
					{{ summary.events.page_builder_by_slug }} matched by slug only (no nominated archive page)
				</div>
				<div v-else class="uds-tile__sub">Page Builder clients all have a nominated archive page</div>
			</div>
			<div class="uds-tile">
				<div class="uds-tile__label">News &amp; Notices</div>
				<div class="uds-tile__split">
					<button class="uds-split" :class="{ 'is-active': filters.news === 'page_builder' }" @click="toggleFilter('news', 'page_builder')">
						<span class="uds-split__num uds-split__num--pb">{{ summary.news.page_builder }}</span>
						<span class="uds-split__lbl">Page Builder</span>
					</button>
					<button class="uds-split" :class="{ 'is-active': filters.news === 'predefined' }" @click="toggleFilter('news', 'predefined')">
						<span class="uds-split__num uds-split__num--pre">{{ summary.news.predefined }}</span>
						<span class="uds-split__lbl">Predefined</span>
					</button>
				</div>
				<div v-if="summary.news.page_builder_by_slug" class="uds-tile__sub uds-tile__sub--warn">
					<font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="me-1" />
					{{ summary.news.page_builder_by_slug }} matched by slug only (no nominated archive page)
				</div>
				<div v-else class="uds-tile__sub">Page Builder clients all have a nominated archive page</div>
			</div>
		</div>

		<!-- Filters -->
		<div v-if="clients.length" class="uds-filters mt-3">
			<div class="uds-search">
				<font-awesome-icon :icon="['fas', 'search']" class="uds-search__icon" />
				<input v-model.trim="search" type="search" class="uds-search__input" placeholder="Search client or URL…" />
			</div>
			<div class="uds-filter-group">
				<span class="uds-filter-group__label">Agendas &amp; Minutes</span>
				<button v-for="opt in modeOptions" :key="'m-' + opt.value" class="uds-chip" :class="{ 'is-active': filters.meetings === opt.value }" @click="filters.meetings = opt.value">{{ opt.label }}</button>
			</div>
			<div class="uds-filter-group">
				<span class="uds-filter-group__label">Events</span>
				<button v-for="opt in modeOptions" :key="'e-' + opt.value" class="uds-chip" :class="{ 'is-active': filters.events === opt.value }" @click="filters.events = opt.value">{{ opt.label }}</button>
			</div>
			<div class="uds-filter-group">
				<span class="uds-filter-group__label">News</span>
				<button v-for="opt in modeOptions" :key="'n-' + opt.value" class="uds-chip" :class="{ 'is-active': filters.news === opt.value }" @click="filters.news = opt.value">{{ opt.label }}</button>
			</div>
			<label class="uds-issues-toggle">
				<input v-model="onlyIssues" type="checkbox" class="form-check-input" />
				Only issues
			</label>
			<button v-if="hasActiveFilters" class="uds-clear" @click="clearFilters">
				<font-awesome-icon :icon="['fas', 'times-circle']" class="me-1" />
				Clear
			</button>
			<span class="uds-count">{{ filteredClients.length }} of {{ clients.length }}</span>
		</div>

		<!-- Table -->
		<div v-if="clients.length" class="uds-table mt-2">
			<div class="uds-row uds-row--head">
				<button class="uds-cell uds-cell--sort" @click="sortBy('name')">
					Client <font-awesome-icon v-if="sort.key === 'name'" :icon="['fas', sort.dir === 'asc' ? 'chevron-down' : 'chevron-up']" class="ms-1" />
				</button>
				<button class="uds-cell uds-cell--sort" @click="sortBy('meetings')">
					Agendas &amp; Minutes <font-awesome-icon v-if="sort.key === 'meetings'" :icon="['fas', sort.dir === 'asc' ? 'chevron-down' : 'chevron-up']" class="ms-1" />
				</button>
				<button class="uds-cell uds-cell--sort" @click="sortBy('events')">
					Events <font-awesome-icon v-if="sort.key === 'events'" :icon="['fas', sort.dir === 'asc' ? 'chevron-down' : 'chevron-up']" class="ms-1" />
				</button>
				<button class="uds-cell uds-cell--sort" @click="sortBy('news')">
					News &amp; Notices <font-awesome-icon v-if="sort.key === 'news'" :icon="['fas', sort.dir === 'asc' ? 'chevron-down' : 'chevron-up']" class="ms-1" />
				</button>
				<button class="uds-cell uds-cell--sort" @click="sortBy('launched_at')">
					Launched <font-awesome-icon v-if="sort.key === 'launched_at'" :icon="['fas', sort.dir === 'asc' ? 'chevron-down' : 'chevron-up']" class="ms-1" />
				</button>
				<div class="uds-cell uds-cell--actions"></div>
			</div>

			<div v-for="client in filteredClients" :key="client.id" class="uds-row">
				<!-- Client -->
				<div class="uds-cell uds-cell--client">
					<router-link :to="'/juniper/clients/edit/' + client.id" class="uds-client-name">{{ client.name }}</router-link>
					<a :href="client.url" target="_blank" rel="noopener" class="uds-client-url">
						<i class="fad fa-globe me-1"></i>{{ stripScheme(client.url) }}
					</a>
					<span class="uds-theme">{{ client.theme }}</span>
				</div>

				<!-- Agendas & Minutes -->
				<div class="uds-cell">
					<span class="uds-badge" :class="'uds-badge--' + client.meetings.mode">
						<i :class="modeIcon(client.meetings.mode)"></i>
						{{ modeLabel(client.meetings.mode) }}
					</span>
					<template v-if="client.meetings.mode === 'predefined'">
						<a :href="client.meetings.predefined_url" target="_blank" rel="noopener" class="uds-page-link">
							/{{ pathOf(client.meetings.predefined_url) }}
							<font-awesome-icon :icon="['fas', 'external-link-alt']" class="ms-1" />
						</a>
						<span v-if="client.meetings.pages.length" class="uds-note">
							+ {{ client.meetings.pages.length }} Page Builder page<span v-if="client.meetings.pages.length !== 1">s</span> with the Meetings element
						</span>
					</template>
					<template v-else>
						<a
							v-for="page in client.meetings.pages"
							:key="page.id"
							:href="page.url"
							target="_blank"
							rel="noopener"
							class="uds-page-link"
							:title="page.title"
						>
							/{{ page.slug }}
							<font-awesome-icon :icon="['fas', 'external-link-alt']" class="ms-1" />
						</a>
						<span v-if="!client.meetings.pages.length" class="uds-issue">
							<font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="me-1" />
							No published page uses the Meetings element
						</span>
					</template>
				</div>

				<!-- Events -->
				<div class="uds-cell">
					<span class="uds-badge" :class="'uds-badge--' + client.events.mode">
						<i :class="modeIcon(client.events.mode)"></i>
						{{ modeLabel(client.events.mode) }}
					</span>
					<template v-if="client.events.page">
						<a :href="client.events.page.url" target="_blank" rel="noopener" class="uds-page-link" :title="client.events.page.title">
							/{{ client.events.page.slug }}
							<font-awesome-icon :icon="['fas', 'external-link-alt']" class="ms-1" />
						</a>
						<span v-if="client.events.source === 'slug'" class="uds-issue" title="No page is nominated as the events archive (Event_archive_page_id); the theme page is skipped only because a published page sits on the events slug.">
							<font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="me-1" />
							Matched by slug only, not nominated
						</span>
					</template>
					<template v-else>
						<a :href="client.events.predefined_url" target="_blank" rel="noopener" class="uds-page-link">
							/{{ pathOf(client.events.predefined_url) }}
							<font-awesome-icon :icon="['fas', 'external-link-alt']" class="ms-1" />
						</a>
						<span v-if="client.events.source === 'nominated_page_unpublished'" class="uds-issue" title="Event_archive_page_id points at a page that is no longer published — the theme page is generated instead.">
							<font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="me-1" />
							Nominated page is not published
						</span>
					</template>
				</div>

				<!-- News & Notices -->
				<div class="uds-cell">
					<span class="uds-badge" :class="'uds-badge--' + client.news.mode">
						<i :class="modeIcon(client.news.mode)"></i>
						{{ modeLabel(client.news.mode) }}
					</span>
					<template v-if="client.news.page">
						<a :href="client.news.page.url" target="_blank" rel="noopener" class="uds-page-link" :title="client.news.page.title">
							/{{ client.news.page.slug }}
							<font-awesome-icon :icon="['fas', 'external-link-alt']" class="ms-1" />
						</a>
						<span v-if="client.news.source === 'slug'" class="uds-issue" title="No page is nominated as the news archive (NewsNotice_archive_page_id); the theme archive is skipped only because a published page sits on the news slug.">
							<font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="me-1" />
							Matched by slug only, not nominated
						</span>
					</template>
					<template v-else>
						<a :href="client.news.predefined_url" target="_blank" rel="noopener" class="uds-page-link">
							/{{ pathOf(client.news.predefined_url) }}
							<font-awesome-icon :icon="['fas', 'external-link-alt']" class="ms-1" />
						</a>
						<span v-if="client.news.source === 'nominated_page_unpublished'" class="uds-issue" title="NewsNotice_archive_page_id points at a page that is no longer published — the theme archive is generated instead.">
							<font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="me-1" />
							Nominated page is not published
						</span>
					</template>
				</div>

				<!-- Launched -->
				<div class="uds-cell uds-cell--muted">
					<span v-if="client.launched_at" :title="client.launched_at">{{ formatDate(client.launched_at) }}</span>
					<span v-else>—</span>
				</div>

				<!-- Actions -->
				<div class="uds-cell uds-cell--actions">
					<router-link :to="'/juniper/clients/edit/' + client.id + '/settings'" class="uds-icon-btn" title="Client settings">
						<font-awesome-icon :icon="['fas', 'cog']" />
					</router-link>
				</div>
			</div>

			<div v-if="!filteredClients.length" class="uds-empty">No clients match the current filters.</div>
		</div>

		<div v-else-if="!loading && !loadError" class="uds-empty mt-3">No live Unified Dashboard clients found.</div>
	</div>
</template>

<script>
import moment from 'moment'
import juniperApi from '@/api/juniperApi'

const MODE_LABELS = {
	page_builder: 'Page Builder',
	predefined: 'Predefined',
}

export default {
	name: 'UnifiedDashboardStats',
	data() {
		return {
			loading: false,
			loadError: null,
			generatedAt: null,
			summary: null,
			clients: [],
			search: '',
			filters: { meetings: 'all', events: 'all', news: 'all' },
			onlyIssues: false,
			sort: { key: 'name', dir: 'asc' },
			modeOptions: [
				{ value: 'all', label: 'All' },
				{ value: 'page_builder', label: 'Page Builder' },
				{ value: 'predefined', label: 'Predefined' },
			],
		}
	},
	computed: {
		hasActiveFilters() {
			return this.search !== '' || this.filters.meetings !== 'all' || this.filters.events !== 'all' || this.filters.news !== 'all' || this.onlyIssues
		},
		filteredClients() {
			const q = this.search.toLowerCase()
			const rows = this.clients.filter(c => {
				if (this.filters.meetings !== 'all' && c.meetings.mode !== this.filters.meetings) return false
				if (this.filters.events !== 'all' && c.events.mode !== this.filters.events) return false
				if (this.filters.news !== 'all' && c.news.mode !== this.filters.news) return false
				if (this.onlyIssues && !this.hasIssue(c)) return false
				if (q && !(c.name || '').toLowerCase().includes(q) && !(c.url || '').toLowerCase().includes(q)) return false
				return true
			})
			const dir = this.sort.dir === 'asc' ? 1 : -1
			const key = this.sort.key
			return rows.sort((a, b) => {
				let av
				let bv
				if (key === 'meetings' || key === 'events' || key === 'news') {
					av = a[key].mode
					bv = b[key].mode
				} else {
					av = a[key] || ''
					bv = b[key] || ''
				}
				const cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' })
				if (cmp === 0) return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
				return cmp * dir
			})
		},
	},
	created() {
		this.load()
	},
	methods: {
		async load() {
			this.loading = true
			this.loadError = null
			try {
				const { data } = await juniperApi.get('statistics/unified-dashboard')
				this.clients = data.clients || []
				this.summary = data.summary || null
				this.generatedAt = data.generated_at || null
			} catch (error) {
				this.loadError = error.response?.data?.message || 'Failed to load Unified Dashboard statistics'
			} finally {
				this.loading = false
			}
		},
		hasIssue(client) {
			return (
				(client.meetings.mode === 'page_builder' && !client.meetings.pages.length) ||
				client.events.source === 'slug' ||
				client.events.source === 'nominated_page_unpublished' ||
				client.news.source === 'slug' ||
				client.news.source === 'nominated_page_unpublished'
			)
		},
		toggleFilter(group, value) {
			this.filters[group] = this.filters[group] === value ? 'all' : value
		},
		clearFilters() {
			this.search = ''
			this.filters = { meetings: 'all', events: 'all', news: 'all' }
			this.onlyIssues = false
		},
		sortBy(key) {
			if (this.sort.key === key) {
				this.sort.dir = this.sort.dir === 'asc' ? 'desc' : 'asc'
			} else {
				this.sort = { key, dir: 'asc' }
			}
		},
		modeLabel(mode) {
			return MODE_LABELS[mode] || mode
		},
		modeIcon(mode) {
			return mode === 'page_builder' ? 'fad fa-puzzle-piece me-1' : 'fad fa-file-alt me-1'
		},
		stripScheme(url) {
			return (url || '').replace(/^https?:\/\//i, '').replace(/\/$/, '')
		},
		pathOf(url) {
			if (!url) return ''
			try {
				return new URL(url).pathname.replace(/^\//, '')
			} catch (e) {
				return url
			}
		},
		fromNow(value) {
			return moment(value).fromNow()
		},
		formatDate(value) {
			return moment(value).format('MMM D, YYYY')
		},
		exportCsv() {
			const esc = v => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`
			const header = ['Client', 'URL', 'Theme', 'Launched', 'Agendas & Minutes', 'Agendas & Minutes pages', 'Events', 'Events page', 'Events source', 'News', 'News page', 'News source']
			const lines = this.filteredClients.map(c =>
				[
					c.name,
					c.url,
					c.theme,
					c.launched_at ? moment(c.launched_at).format('YYYY-MM-DD') : '',
					this.modeLabel(c.meetings.mode),
					c.meetings.mode === 'predefined' ? c.meetings.predefined_url : c.meetings.pages.map(p => p.url).join(' | '),
					this.modeLabel(c.events.mode),
					c.events.page ? c.events.page.url : c.events.predefined_url,
					c.events.source,
					this.modeLabel(c.news.mode),
					c.news.page ? c.news.page.url : c.news.predefined_url,
					c.news.source,
				]
					.map(esc)
					.join(','),
			)
			const csv = [header.map(esc).join(','), ...lines].join('\n')
			const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
			const link = document.createElement('a')
			link.href = URL.createObjectURL(blob)
			link.download = `unified-dashboard-stats-${moment().format('YYYY-MM-DD')}.csv`
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			URL.revokeObjectURL(link.href)
		},
	},
}
</script>

<style scoped>
.uds-toolbar {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
}

.uds-toolbar__actions {
	display: inline-flex;
	gap: 8px;
}

.uds-intro {
	font-size: 13px;
	color: #64748b;
	max-width: 720px;
}

.uds-fetched {
	color: #94a3b8;
}

.uds-alert {
	font-size: 13px;
	color: #92400e;
	background: #fffbeb;
	border: 1px solid #fde68a;
	border-radius: 10px;
	padding: 8px 12px;
}

.uds-loading,
.uds-empty {
	font-size: 13px;
	color: #64748b;
}

.uds-empty {
	padding: 24px 14px;
	text-align: center;
}

/* Summary tiles */
.uds-tiles {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
	gap: 12px;
}

.uds-tile {
	background: #ffffff;
	border: 1px solid #e2e8f0;
	border-radius: 14px;
	padding: 14px 16px;
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.uds-tile__label {
	font-size: 12px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	color: #64748b;
}

.uds-tile__value {
	font-size: 28px;
	font-weight: 700;
	color: #0f172a;
	line-height: 1.1;
}

.uds-tile__sub {
	font-size: 12px;
	color: #94a3b8;
}

.uds-tile__sub--warn {
	color: #b45309;
}

.uds-tile__split {
	display: flex;
	gap: 8px;
}

.uds-split {
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 2px;
	background: #f8fafc;
	border: 1px solid #e2e8f0;
	border-radius: 10px;
	padding: 8px 10px;
	cursor: pointer;
	text-align: left;
	transition: border-color 0.15s, background 0.15s;
}

.uds-split:hover {
	border-color: #cbd5e1;
}

.uds-split.is-active {
	background: #eef2ff;
	border-color: #a5b4fc;
}

.uds-split__num {
	font-size: 22px;
	font-weight: 700;
	line-height: 1.1;
}

.uds-split__num--pb {
	color: #0f766e;
}

.uds-split__num--pre {
	color: #b45309;
}

.uds-split__lbl {
	font-size: 12px;
	color: #64748b;
}

/* Filters */
.uds-filters {
	display: flex;
	align-items: center;
	gap: 14px;
	flex-wrap: wrap;
	font-size: 13px;
}

.uds-search {
	position: relative;
	flex: 1 1 220px;
	max-width: 320px;
}

.uds-search__icon {
	position: absolute;
	left: 10px;
	top: 50%;
	transform: translateY(-50%);
	color: #94a3b8;
	font-size: 12px;
}

.uds-search__input {
	width: 100%;
	border: 1px solid #e2e8f0;
	border-radius: 10px;
	padding: 7px 10px 7px 30px;
	font-size: 13px;
	background: #fff;
}

.uds-search__input:focus {
	outline: none;
	border-color: #a5b4fc;
	box-shadow: 0 0 0 3px rgba(165, 180, 252, 0.3);
}

.uds-filter-group {
	display: inline-flex;
	align-items: center;
	gap: 4px;
}

.uds-filter-group__label {
	font-size: 12px;
	color: #64748b;
	margin-right: 4px;
}

.uds-chip {
	border: 1px solid #e2e8f0;
	background: #fff;
	color: #475569;
	border-radius: 999px;
	padding: 3px 10px;
	font-size: 12px;
	font-weight: 600;
	cursor: pointer;
}

.uds-chip:hover {
	border-color: #cbd5e1;
}

.uds-chip.is-active {
	background: #1e293b;
	border-color: #1e293b;
	color: #fff;
}

.uds-issues-toggle {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	color: #475569;
	cursor: pointer;
	margin: 0;
}

.uds-issues-toggle .form-check-input {
	margin: 0;
}

.uds-clear {
	border: none;
	background: transparent;
	color: #64748b;
	font-size: 12px;
	cursor: pointer;
	padding: 0;
}

.uds-clear:hover {
	color: #0f172a;
}

.uds-count {
	margin-left: auto;
	font-size: 12px;
	color: #94a3b8;
}

/* Table */
.uds-table {
	background: #ffffff;
	border: 1px solid #e2e8f0;
	border-radius: 14px;
	overflow: hidden;
}

.uds-row {
	display: grid;
	grid-template-columns: minmax(220px, 1.4fr) minmax(180px, 1.1fr) minmax(180px, 1.1fr) minmax(180px, 1.1fr) 120px 48px;
	gap: 12px;
	padding: 12px 14px;
	border-top: 1px solid #f1f5f9;
	align-items: start;
}

.uds-row--head {
	border-top: none;
	background: #f8fafc;
	padding-top: 10px;
	padding-bottom: 10px;
}

.uds-cell {
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 0;
	font-size: 13px;
	color: #0f172a;
}

.uds-cell--sort {
	flex-direction: row;
	align-items: center;
	border: none;
	background: transparent;
	padding: 0;
	font-size: 12px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	color: #64748b;
	cursor: pointer;
	text-align: left;
}

.uds-cell--sort:hover {
	color: #0f172a;
}

.uds-cell--muted {
	color: #64748b;
}

.uds-cell--actions {
	align-items: flex-end;
}

.uds-client-name {
	font-weight: 600;
	color: #0f172a;
	text-decoration: none;
}

.uds-client-name:hover {
	color: #4338ca;
}

.uds-client-url {
	font-size: 12px;
	color: #64748b;
	text-decoration: none;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.uds-client-url:hover {
	color: #0f172a;
}

.uds-theme {
	display: inline-block;
	align-self: flex-start;
	font-size: 11px;
	color: #64748b;
	background: #f1f5f9;
	border-radius: 6px;
	padding: 1px 6px;
}

.uds-badge {
	display: inline-flex;
	align-items: center;
	align-self: flex-start;
	font-size: 12px;
	font-weight: 600;
	padding: 3px 9px;
	border-radius: 999px;
	white-space: nowrap;
}

.uds-badge--page_builder {
	color: #0f766e;
	background: #ccfbf1;
	border: 1px solid #99f6e4;
}

.uds-badge--predefined {
	color: #b45309;
	background: #fef3c7;
	border: 1px solid #fde68a;
}

.uds-page-link {
	font-size: 12px;
	color: #475569;
	text-decoration: none;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.uds-page-link:hover {
	color: #4338ca;
}

.uds-note {
	font-size: 12px;
	color: #94a3b8;
}

.uds-issue {
	font-size: 12px;
	color: #b91c1c;
}

.uds-icon-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 30px;
	height: 30px;
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	color: #64748b;
	background: #fff;
	text-decoration: none;
}

.uds-icon-btn:hover {
	color: #0f172a;
	border-color: #cbd5e1;
}

@media (max-width: 900px) {
	.uds-row {
		grid-template-columns: 1fr;
	}

	.uds-row--head {
		display: none;
	}

	.uds-cell--actions {
		align-items: flex-start;
	}
}
</style>
